import React from "react";
import { useNavigate } from "react-router-dom";

import {
  Delete,
  Check,
  Launch,
  Clear, RestartAlt,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";

import { ButtonIcon, HelpBox } from "@/components/atoms";
import { useActivationActions, useAudienceActions, useNotificationContext } from "@/context";
import { Notification } from "@/types";
import { formatDate } from "@/utils";

interface NotificationMenuProps {
  anchorEl: null | HTMLElement;
  onClose: () => void;
}

const NotificationMenu = ({ anchorEl, onClose }: NotificationMenuProps) => {

  const { notifications, refresh, markAsRead, markAllAsRead } = useNotificationContext();
  const audienceActions = useAudienceActions();
  const activationActions = useActivationActions();
  const navigate = useNavigate();

  const ACTION_CATEGORIES = ['Audience', 'Upload', 'Activation', 'Travel-Time', 'Traveltime'];

  const handleSelect = (n: Notification) => {
    // The notification opens a saved audience or activation, so drop any being edited
    audienceActions.reset();
    activationActions.reset();
    if (n.category === 'Audience') {
      navigate(`/audience/${n.data.audience_id}/insight`);
    } else if (n.category === 'Upload') {
      navigate(`/upload/${n.data.audience_id}/insight`);
    } else if (n.category === 'Activation') {
      if (n.data.campaign_type === 'meta') {
        navigate(`/activation/${n.data.campaign_id}/recipe`)
      } else {
        navigate(`/activation/${n.data.campaign_id}`)
      }
    } else if (n.category === 'Travel-Time') {
      navigate(`/upload/${n.data.audience_id}/insight`);
    } else if (n.category === 'Traveltime') {
      navigate(`/upload/${n.data.audience_id}/insight`);
    } else {
      console.log(n.id)
    }
  }

  return (
    <Menu
      id="notifications-menu"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            width: 320,
            mt: 1,
          }
        }
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Typography>
          Notifications
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {notifications && notifications.length > 0 && (
            <Typography variant="caption" color="primary">
              {notifications.length} New
            </Typography>
          )}
          <HelpBox title="Refresh Notifications">
            <IconButton
              size="small"
              onClick={refresh}
              sx={{
                borderRadius: "6px"
              }}
            >
              <RestartAlt fontSize="small" />
            </IconButton>
          </HelpBox>
        </Stack>
      </Box>

      <Divider />

      <Box
        sx={{
          maxHeight: 400,
          overflowY: "auto"
        }}
      >
        {notifications && notifications.length > 0 ? (
          notifications.map((n, idx) => (
            <Stack
              key={n.id}
              spacing={1}
              sx={{
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Chip
                  label={n.category}
                  size="small"
                  color={n.category.includes('Error' ) ? "error" : "default"}
                />
                <Typography variant="body2" color="textSecondary">
                  {formatDate(new Date(n.created_at))}
                </Typography>
              </Stack>



              <Typography>
                {n.message}
              </Typography>

              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={0}
              >
                <HelpBox title="Clear">
                  <IconButton
                    size="small"
                    onClick={() => markAsRead(n.id)}
                    sx={{
                      borderRadius: "6px"
                    }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </HelpBox>
                {ACTION_CATEGORIES.includes(n.category) && (
                  <HelpBox title="Open">
                    <IconButton
                      size="small"
                      onClick={() => handleSelect(n)}
                      sx={{
                        borderRadius: "6px"
                      }}
                    >
                      <Launch fontSize="small" />
                    </IconButton>
                  </HelpBox>
                )}
              </Stack>
            </Stack>
          ))
        ) : (
          <Box
            sx={{
              p: 3,
              textAlign: "center"
            }}
          >
            <Typography variant="body2" color="textSecondary">
              No more notifications
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        pt={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          variant="text"
          size="small"
          color="inherit"
          onClick={() => markAllAsRead()}
        >
          Clear All
        </Button>
      </Box>

    </Menu>
  );
};

export default NotificationMenu;
